import React, { FC, MutableRefObject, Suspense, useContext, useEffect, useRef, useState } from "react";
import { CancelTokenSource } from "axios";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { apiOrganization } from "src/services/Api/Organization/apiOrganization";
import { convertListStatusToEntityStatus } from "src/utils/entityStatus/entityStatus";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import { StickyColumnWithEntityStatus } from "go-core/components/StickyColumnWithEntityStatus";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { FILTER_SEPARATOR, NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { ListData } from "go-list/list";
import { listDataParams } from "go-list/list/services/decoder";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig, ListParamsType, MultipleActionsParams } from "go-list/list/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { OrganizationState } from "go-security/services/organizations/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext, SegmentListService } from "go-segment/context";
import { ProductApi } from "../../../../../../../../../../services/Api/Organization/types";
import { ProductMultipleActionsModal } from "../../../../components/ProductMultipleActionsModal";
import { ModalMultipleActionType } from "../../../../services/types";

interface ListState {
	resource?: Record<string, any>;
	mobileActions: MobileActionProps[];
}

const listName = "PACKAGE";
const resourceType = "PACKAGE";

const getFormattedParams = (currentParams: ListParamsType | undefined) => {
	const formattedParams = currentParams ? listDataParams(currentParams) : {};
	formattedParams.include = "all";
	const filterFromParams = decodeURIComponent(escape(atob(formattedParams?.f || "")));
	const prefix = filterFromParams.startsWith(NEW_WAY_TO_ENCODING_FILTER_SIGN) ? "" : NEW_WAY_TO_ENCODING_FILTER_SIGN;
	const encodedParams = btoa(
		unescape(encodeURIComponent(`${prefix}${filterFromParams}${FILTER_SEPARATOR}product_type|e=PACKAGE`))
	);
	formattedParams.f = encodedParams;

	return { formattedParams, encodedParams };
};

const buildConfig = (
	t: TFunction,
	data: Record<string, any>,
	organization: OrganizationState,
	segmentListService: SegmentListService,
	areAllItemsSelectedRef: MutableRefObject<boolean>,
	multipleActionParamsRef: MutableRefObject<Record<string, any> | undefined>,
	listParamsRef: MutableRefObject<Record<string, any> | undefined>,
	onRemove: (
		id: number | string,
		currentParams?: ListParamsType,
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => Promise<void>,
	onRestore: (
		id: number | string,
		currentParams?: ListParamsType,
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => Promise<void>,
	onCloseLinkedProducts: (
		id: number | string,
		currentParams?: ListParamsType,
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => Promise<void>,
	setAssignMeasureTypeProductIds: (productIds: string) => void,
	setAssignCategoryProductIds: (productIds: string) => void,
	setAssignCustomFieldsProductIds: (productIds: string) => void
) => {
	let config = {
		fields: [
			{
				id: "name",
				name: t("common.word.name", { ns: "lib" }),
				render: (item: ProductApi) => (
					<StickyColumnWithEntityStatus
						status={convertListStatusToEntityStatus(item.status)}
						to={`/${organization.id}/products/${item.id}/edit`}
					>
						{item.name}
					</StickyColumnWithEntityStatus>
				),
				renderExport: (item: ProductApi) => item.name,
			},
			{
				id: "description",
				name: t("common.word.description", { ns: "lib" }),
				type: "text",
			},
			{
				id: "category",
				name: t("common.word.category"),
				render: (item: ProductApi) => item?.category?.name,
			},
			{
				id: "sku",
				name: t("modules.product.field.sku.title"),
				type: "text",
			},
			{
				id: "cost_price_money",
				name: t("modules.product.field.cost_price_money.title"),
				render: (item: ProductApi) => FormatMoney(item.cost_price_money),
			},
			{
				id: "sale_price_net_money",
				name: t("modules.product.field.sale_price_net_money.title"),
				render: (item: ProductApi) => FormatMoney(item.sale_price_net_money),
			},
		],
		actions: [
			{
				name: t("common.action.edit", { ns: "lib" }),
				link: (item: ProductApi) => `/${organization.id}/products/${item.id}/edit`,
			},
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: (item: ProductApi, params?: ListParamsType) => onRemove(item.id, params),
				visible: (item: ProductApi) => item.status === "ENABLED",
				refreshListAfter: true,
			},
			{
				name: t("common.action.restore", { ns: "lib" }),
				click: (item: ProductApi, params?: ListParamsType) => onRestore(item.id, params),
				visible: (item: ProductApi) => item.status !== "ENABLED",
				refreshListAfter: true,
			},
			{
				name: t("modules.product.action.close_linked_products.title"),
				click: (item: ProductApi, params?: ListParamsType) => onCloseLinkedProducts(item.id, params),
				visible: (item: ProductApi) => item.open_linkeds,
				refreshListAfter: true,
			},
		],
		selectedColumns: ["name", "category", "description", "sku", "cost_price_money", "sale_price_net_money"],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
			{
				id: "deleted",
				name: t("go_list.filters.deleted", { ns: "lib" }),
				slug: "deleted",
				filters: [
					{
						filterId: "statuses",
						value: "DELETED",
						condition: "e",
					},
				],
			},
		],
		filters: [
			{
				id: "products_in_recipe",
				name: t("modules.package.field.products_in_recipe.title"),
				type: "search_select",
				source: {
					request: async (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getProductSearchSelect(search, params, { cancelToken: options?.cancelToken }),
				},
			},
			{
				id: "statuses",
				name: t("common.word.status", { ns: "lib" }),
				type: "list",
				listOptions: {
					ENABLED: t("enums.common.status.ENABLED", { ns: "lib" }),
					DELETED: t("enums.common.status.DELETED", { ns: "lib" }),
					DISABLED: t("enums.common.status.DISABLED", { ns: "lib" }),
				},
				hasDefaultOptions: false,
			},
			{
				id: "measure_type",
				name: t("common.word.measure_type"),
				type: "list",
				listOptions: {
					KILOGRAM: t("enums.products.measure_types.KILOGRAM"),
					LITER: t("enums.products.measure_types.LITER"),
					ITEM: t("enums.products.measure_types.ITEM"),
					PACKAGE: t("enums.products.measure_types.PACKAGE"),
				},
			},
			{
				id: "categories",
				name: t("common.word.category"),
				type: "search_select",
				source: {
					request: async (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getProductCategorySearchSelect(search, params, {
							cancelToken: options?.cancelToken,
						}),
				},
			},
			{
				id: "salePrice",
				name: t("modules.product.field.sale_price_net_money.title"),
				type: "number",
			},
			{
				id: "barcode",
				name: t("common.word.barcode"),
				type: "text",
			},
		],
		exportConfig: {
			title: t("modules.package.header.title"),
			filename: t("modules.package.header.title"),
			organization: organization.name,
		},
		multipleActions: [
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: async (
					multipleActionsItems: ProductApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => {
					const ids = multipleActionsItems.map((item) => item.id).join(",");
					await onRemove(ids, listParams, areAllItemsSelected, multipleActionParams);
				},
				visible: (multipleActionsItems: ProductApi[]) =>
					multipleActionsItems.filter((item) => item.status !== "DELETED").length > 0,
				refreshListAfter: true,
			},
			{
				name: t("common.action.restore", { ns: "lib" }),
				click: async (
					multipleActionsItems: ProductApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => {
					const ids = multipleActionsItems.map((item) => item.id).join(",");
					await onRestore(ids, listParams, areAllItemsSelected, multipleActionParams);
				},
				visible: (multipleActionsItems: ProductApi[]) =>
					multipleActionsItems.filter((item) => item.status !== "ENABLED").length > 0,
				refreshListAfter: true,
			},
			{
				name: t("modules.product.action.close_linked_products.title"),
				click: async (
					multipleActionsItems: ProductApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => {
					const ids = multipleActionsItems.map((item) => item.id).join(",");
					await onCloseLinkedProducts(ids, listParams, areAllItemsSelected, multipleActionParams);
				},
				visible: (multipleActionsItems: ProductApi[]) =>
					multipleActionsItems.filter((item) => item.open_linkeds).length > 0,
				refreshListAfter: true,
			},
			{
				name: t("modules.product.action.assign_measure_type.title"),
				click: (
					multipleActionsItems: ProductApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => {
					const idsAsString = multipleActionsItems.map((item) => item.id).join(",");
					areAllItemsSelectedRef.current = Boolean(areAllItemsSelected);
					multipleActionParamsRef.current = multipleActionParams;
					listParamsRef.current = listParams;
					setAssignMeasureTypeProductIds(idsAsString);
				},
			},
			{
				name: t("modules.product.action.assign_category.title"),
				click: (
					multipleActionsItems: ProductApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => {
					const idsAsString = multipleActionsItems.map((item) => item.id).join(",");
					areAllItemsSelectedRef.current = Boolean(areAllItemsSelected);
					multipleActionParamsRef.current = multipleActionParams;
					listParamsRef.current = listParams;
					setAssignCategoryProductIds(idsAsString);
				},
			},
			{
				name: t("modules.product.action.assign_custom_fields.title"),
				click: (
					multipleActionsItems: ProductApi[],
					listParams?: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionParams?: MultipleActionsParams
				) => {
					const idsAsString = multipleActionsItems.map((item) => item.id).join(",");
					areAllItemsSelectedRef.current = Boolean(areAllItemsSelected);
					multipleActionParamsRef.current = multipleActionParams;
					listParamsRef.current = listParams;
					setAssignCustomFieldsProductIds(idsAsString);
				},
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			const filterFromParams = decodeURIComponent(escape(atob(params?.f || "")));
			params.include = "all";
			const prefix = filterFromParams.startsWith(NEW_WAY_TO_ENCODING_FILTER_SIGN)
				? ""
				: NEW_WAY_TO_ENCODING_FILTER_SIGN;
			const productTypeFilter = filterFromParams.includes("product_type")
				? ""
				: `${FILTER_SEPARATOR}product_type|e=PACKAGE`;
			params.f = btoa(unescape(encodeURIComponent(`${prefix}${filterFromParams}${productTypeFilter}`)));

			return apiOrganization.getProducts(params, { cancelToken: sourceToken?.token });
		},
		fetchTotalItemsCountForSelectedFilters: (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			const filterFromParams = decodeURIComponent(escape(atob(params?.f || "")));
			const prefix = filterFromParams.startsWith(NEW_WAY_TO_ENCODING_FILTER_SIGN)
				? ""
				: NEW_WAY_TO_ENCODING_FILTER_SIGN;
			params.include = "all";
			params.f = btoa(
				unescape(encodeURIComponent(`${prefix}${filterFromParams}${FILTER_SEPARATOR}product_type|e=PACKAGE`))
			);

			return apiOrganization.getProductsCount(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => segmentListService.save(listName, resourceType, segment),
		doesIdColumnRedirectToPreviewPage: true,
	} as ListConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};
	return config;
};
const List: FC<ListState> = ({ resource, mobileActions }) => {
	const [responseData, setResponseData] = useState<ProductApi[] | undefined>(undefined);
	const [assignMeasureTypeProductIds, setAssignMeasureTypeProductIds] = useState<string>();
	const [assignCategoryProductIds, setAssignCategoryProductIds] = useState<string>();
	const [assignCustomFieldsProductIds, setAssignCustomFieldsProductIds] = useState<string>();
	const areAllItemsSelectedRef = useRef<boolean>(false);
	const multipleActionParamsRef = useRef<Record<string, any> | undefined>({});
	const listParamsRef = useRef<ListParamsType | undefined>(undefined);
	const segmentContextValue = useContext(SegmentContext);
	const { t } = useTranslation();
	const history = useHistory();
	const organization = useSelector(selectOrganization);
	const confirmation = useConfirmation();
	const { addSuccessFlash } = useFlash();

	if (!resource) return null;

	const onRestore = async (
		id: number | string,
		currentParams?: ListParamsType,
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const { formattedParams, encodedParams } = getFormattedParams(currentParams);
		if (multipleActionParams) multipleActionParams.f = encodedParams;

		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: t("confirmation.message.activate", { ns: "lib" }),
		});
		if (typeof id === "string")
			await apiOrganization.restoreMultipleProducts(id, multipleActionParams, areAllItemsSelected);
		else await apiOrganization.restoreProduct(id, formattedParams);
		addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
	};

	const onRemove = async (
		id: number | string,
		currentParams?: ListParamsType,
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const { formattedParams, encodedParams } = getFormattedParams(currentParams);
		if (multipleActionParams) multipleActionParams.f = encodedParams;

		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: t("confirmation.message.remove", { ns: "lib" }),
		});
		if (typeof id === "string")
			await apiOrganization.removeMultipleProducts(id, multipleActionParams, areAllItemsSelected);
		else await apiOrganization.removeProduct(id, formattedParams);
		addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
	};

	const onCloseLinkedProducts = async (
		id: number | string,
		currentParams?: ListParamsType,
		areAllItemsSelected?: boolean,
		multipleActionParams?: MultipleActionsParams
	) => {
		const { formattedParams, encodedParams } = getFormattedParams(currentParams);
		if (multipleActionParams) multipleActionParams.f = encodedParams;

		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});
		if (typeof id === "string")
			await apiOrganization.closeMultipleLinkedProduct(id, multipleActionParams, areAllItemsSelected);
		else await apiOrganization.closeLinkedProduct(id, formattedParams);
		addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
	};

	const refresh = async (currentParams: Record<string, any>) => {
		if (config.fetch) {
			const fetchResponse = await config.fetch(currentParams);
			setResponseData(fetchResponse.data);
		}
	};

	const onModalHide = async () => {
		const { formattedParams } = getFormattedParams(listParamsRef.current);
		await refresh(formattedParams);
	};

	const data = resource.read();
	const config = buildConfig(
		t,
		data,
		organization,
		segmentContextValue,
		areAllItemsSelectedRef,
		multipleActionParamsRef,
		listParamsRef,
		onRemove,
		onRestore,
		onCloseLinkedProducts,
		setAssignMeasureTypeProductIds,
		setAssignCategoryProductIds,
		setAssignCustomFieldsProductIds
	);

	return (
		<>
			{assignMeasureTypeProductIds && (
				<ProductMultipleActionsModal
					show={assignMeasureTypeProductIds !== ""}
					onHide={() => setAssignMeasureTypeProductIds("")}
					productIds={assignMeasureTypeProductIds}
					handleSave={onModalHide}
					allItemsSelected={areAllItemsSelectedRef.current}
					multipleActionParams={multipleActionParamsRef.current}
					multipleActionType={ModalMultipleActionType.ASSIGN_MEASURE_TYPE}
					productType="PACKAGE"
				/>
			)}
			{assignCategoryProductIds && (
				<ProductMultipleActionsModal
					show={assignCategoryProductIds !== ""}
					onHide={() => setAssignCategoryProductIds("")}
					productIds={assignCategoryProductIds}
					handleSave={onModalHide}
					allItemsSelected={areAllItemsSelectedRef.current}
					multipleActionParams={multipleActionParamsRef.current}
					multipleActionType={ModalMultipleActionType.ASSIGN_CATEGORY}
					productType="PACKAGE"
				/>
			)}
			{assignCustomFieldsProductIds && (
				<ProductMultipleActionsModal
					show={assignCustomFieldsProductIds !== ""}
					onHide={() => setAssignCustomFieldsProductIds("")}
					productIds={assignCustomFieldsProductIds}
					handleSave={onModalHide}
					allItemsSelected={areAllItemsSelectedRef.current}
					multipleActionParams={multipleActionParamsRef.current}
					multipleActionType={ModalMultipleActionType.ASSIGN_CUSTOM_FIELDS}
					productType="PACKAGE"
				/>
			)}
			<ListData
				data={responseData}
				config={config}
				onFetch={(items: ProductApi[]) => setResponseData(items)}
				emptyList={{
					addAction: () =>
						history.push({
							pathname: `/${organization.id}/products/new`,
							state: { type: "PACKAGE" },
						}),
				}}
				canManageMultiActionsForAllItems
				mobileActions={mobileActions}
			/>
		</>
	);
};

const OrganizationProductPackageIndexPage = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = React.useContext(SegmentContext);
	const organization = useSelector(selectOrganization);
	const { t } = useTranslation();
	const customFieldResourceTypes = [
		{
			type: "PRODUCT",
			name: t(`enums.custom_fields.resources.PRODUCT`),
		},
	];
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(t("modules.package.header.title"));
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType, customFieldResourceTypes)));
	}, []);

	const buttons: ButtonProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: `/${organization.id}/products/new`,
			variant: "primary",
			state: { type: "PACKAGE" },
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: `/${organization.id}/products/new`,
			state: { type: "PACKAGE" },
		},
	];

	return (
		<>
			{!isMobile && <Header title={t("modules.package.header.title")} buttons={buttons} />}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} mobileActions={mobileActions} />
			</Suspense>
		</>
	);
};
export default OrganizationProductPackageIndexPage;
